/**
 * One bounded pool of decode workers shared by every Potree v2 point cloud.
 *
 * `@pnext/three-loader` gives each cloud's `OctreeLoader` its OWN `WorkerPool`,
 * which starts a new Web Worker whenever none is idle and never terminates
 * one. Each cloud decodes up to 3 nodes at once, so N clouds kept up to 3·N
 * workers alive, and toggling a layer off and on built a fresh loader (and
 * pool) without freeing the old one's. On iOS every worker is a separate JS
 * heap with its own decode buffers; a project with several clearance clouds
 * on top of the survey crashed the tab (Safari reloads near ~1.5 GB).
 *
 * The loader's decoders take a worker synchronously, set `onmessage` and post
 * one message, so a busy worker cannot be handed to a second decode (it would
 * steal the first one's reply). `getWorker` therefore returns a stand-in that
 * QUEUES its message until one of at most `maxWorkers` real workers is free.
 */
import { isMobileDevice } from '@/lib/device';

/** The loader's pool, as the decoders use it. Not exported by the package. */
interface WorkerPoolLike {
  getWorker(workerType: string): Worker;
  returnWorker(workerType: string, worker: Worker): void;
}

type QueuedJob = (worker: Worker) => void;

class QueuedWorker {
  onmessage: ((event: MessageEvent) => void) | null = null;
  onerror: ((event: ErrorEvent) => void) | null = null;

  private readonly pool: SharedWorkerPool;
  private readonly workerType: string;

  constructor(pool: SharedWorkerPool, workerType: string) {
    this.pool = pool;
    this.workerType = workerType;
  }

  postMessage(message: unknown, transfer: Transferable[] = []) {
    this.pool.run(this.workerType, (worker) => {
      worker.onmessage = (event) => {
        this.pool.release(this.workerType, worker);
        this.onmessage?.(event);
      };
      worker.onerror = (event) => {
        this.pool.release(this.workerType, worker);
        this.onerror?.(event);
      };
      worker.postMessage(message, transfer);
    });
  }

  terminate() {}
}

export class SharedWorkerPool implements WorkerPoolLike {
  private readonly idle = new Map<string, Worker[]>();
  private readonly queue = new Map<string, QueuedJob[]>();
  private readonly created = new Map<string, number>();

  /**
   * @param factory A pristine `WorkerPool` of the loader's own class: asking it
   *   for a worker while it holds none creates one of that type.
   */
  private readonly factory: WorkerPoolLike;
  readonly maxWorkers: number;

  constructor(factory: WorkerPoolLike, maxWorkers: number) {
    this.factory = factory;
    this.maxWorkers = maxWorkers;
  }

  getWorker(workerType: string): Worker {
    return new QueuedWorker(this, workerType) as unknown as Worker;
  }

  /**
   * The decoders hand back what `getWorker` gave them (a stand-in: nothing to
   * do), but a decode already in flight when a cloud was adopted returns one
   * of its old pool's REAL workers here. Keep it if there is room, else end it.
   */
  returnWorker(workerType: string, worker: Worker) {
    if (worker instanceof QueuedWorker) return;
    if ((this.created.get(workerType) ?? 0) < this.maxWorkers) {
      this.created.set(workerType, (this.created.get(workerType) ?? 0) + 1);
      this.release(workerType, worker);
    } else {
      worker.terminate();
    }
  }

  run(workerType: string, job: QueuedJob) {
    const idle = this.idle.get(workerType);
    const worker = idle?.pop();
    if (worker) return job(worker);
    const count = this.created.get(workerType) ?? 0;
    if (count < this.maxWorkers) {
      this.created.set(workerType, count + 1);
      return job(this.factory.getWorker(workerType));
    }
    const jobs = this.queue.get(workerType) ?? [];
    jobs.push(job);
    this.queue.set(workerType, jobs);
  }

  release(workerType: string, worker: Worker) {
    const next = this.queue.get(workerType)?.shift();
    if (next) return next(worker);
    const idle = this.idle.get(workerType) ?? [];
    idle.push(worker);
    this.idle.set(workerType, idle);
  }

  /** Real workers alive, per type (tests and debugging). */
  workerCount(workerType: string): number {
    return this.created.get(workerType) ?? 0;
  }
}

/** Phones: two decoders; elsewhere one per spare core, up to six. */
export function sharedPoolSize(): number {
  if (isMobileDevice()) return 2;
  const cores = typeof navigator !== 'undefined' ? navigator.hardwareConcurrency || 4 : 4;
  return Math.max(2, Math.min(6, cores - 1));
}

let shared: SharedWorkerPool | null = null;

/**
 * Points a freshly loaded v2 cloud's loader at the shared pool, and ends the
 * idle workers its own pool already started (loading the root node takes one).
 *
 * `loadingContext` and the pool's `workers` are private in the package's
 * types, hence the casts; the shape is pinned by this module's tests.
 */
export function adoptSharedWorkerPool(nodeLoader: unknown) {
  const context = (nodeLoader as { loadingContext?: { workerPool?: WorkerPoolLike } } | null)?.loadingContext;
  const own = context?.workerPool;
  if (!context || !own || own instanceof SharedWorkerPool) return;
  if (!shared) {
    const Pool = own.constructor as new () => WorkerPoolLike;
    shared = new SharedWorkerPool(new Pool(), sharedPoolSize());
  }
  context.workerPool = shared;
  const idleByType = (own as unknown as { workers?: Record<string, Worker[]> }).workers;
  if (idleByType) {
    for (const list of Object.values(idleByType)) {
      for (const worker of list.splice(0)) worker.terminate();
    }
  }
}
